import React, { useEffect, useState } from 'react';
import FilterPreviewer from './FilterPreviewer';
import { generateId } from '../utils/genericUtils';
import { buildFilter } from '../utils/sqlUtils';
import ExpressionTemplate from '../defaults/ExpressionTemplate';
import { FormControl, Tooltip, Button, ButtonGroup } from '@mui/material';
import PlusIcon from '../icons/PlusIcon';
import { StyledSetHeaderRow, StyledAddSetButton } from '../containers/Set-styled';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import FieldNames from '../../../defaults/FieldNames.json';
import _ from "lodash";
import CustomFilterSets from './CustomFilterSets';
import ColorConstants from '../../../defaults/ColorConstants';

const theme = createTheme({
  overrides: {}
});

export default function ArcgisFilter ({ filterForEdit, fields, disableUnique, onChange, userInfo, filterTitleLocal, open, datasetSelection }) {
  const [mustMatchAll, setMustMatchAll] = useState(filterForEdit ? filterForEdit[FieldNames.customFilter.mustMatchAll] : true);
  const [expression] = useState(filterForEdit ? filterForEdit[FieldNames.customFilter.expression] : "");
  const [sets, setSets] = useState(filterForEdit ? filterForEdit[FieldNames.customFilter.sets] : {
    [generateId()]: {
      mustMatchAll: true,
      expression: "",
      expressions: {
        [generateId()]: ExpressionTemplate
      }
    }
  })

  useEffect(() => {
    if(!filterForEdit){
      const currentFilter = {
        [FieldNames.customFilter.expression]: expression, 
        [FieldNames.customFilter.isActive]: "No",
        [FieldNames.customFilter.mustMatchAll]: mustMatchAll,
        [FieldNames.customFilter.sets]: sets,
        [FieldNames.customFilter.title]: filterTitleLocal,
        [FieldNames.customFilter.userID]: userInfo ? userInfo[FieldNames.userFields.userID] : null,
        [FieldNames.customFilter.userCustomFiltersID]: generateId()
      }
      onChangeLocal(currentFilter);
      //We don't set filterForEdit because it needs to only have been set when we are accessing an existing filter
      //If we set it here instead of doing an add when we do an update.  That will fail without an objectId
      //setFilterForEdit(currentFilter);
    } else {
      let tempFilterForEdit = _.cloneDeep(filterForEdit);
      if(open){
        tempFilterForEdit[FieldNames.customFilter.mustMatchAll] = mustMatchAll;
        tempFilterForEdit[FieldNames.customFilter.expression] = expression;
        tempFilterForEdit[FieldNames.customFilter.sets] = sets;
        onChangeLocal(tempFilterForEdit);
      }
    }
  }, [expression, mustMatchAll, sets])

  const onChangeLocal = currentVal => {
    //currentVal is the object
    //filterExpr is a string for the bottom of the screen
    const filterExpr = buildFilter(currentVal, false, userInfo);
    onChange(filterExpr, currentVal);
  };

  const updateSets = currentSets => {
    setSets(currentSets);
    let currentSetObj = {[FieldNames.customFilter.mustMatchAll]: mustMatchAll, [FieldNames.customFilter.expression]: expression, [FieldNames.customFilter.sets]: currentSets}
    let filterToProcess = Object.assign({}, filterForEdit ?? {}, currentSetObj);
    onChangeLocal(filterToProcess);
  };

  const handleAddSet = () => {
    const currentSets = JSON.parse(JSON.stringify(sets));
    //This is being added as a key value and the existing one doesn't have the key.
    currentSets[generateId()] = {
      mustMatchAll: true,
      expressions: {
        [generateId()]: ExpressionTemplate
      }
    };
    updateSets(currentSets);
  };

  const handleRemoveSet = setId => {
    const currentSets = JSON.parse(JSON.stringify(sets));
    delete currentSets[setId];
    updateSets(currentSets);
  };

  const handleUpdateSetOperator = (setId, setOperator) => {
    const currentSets = JSON.parse(JSON.stringify(sets));
    currentSets[setId].mustMatchAll = setOperator;
    updateSets(currentSets);
  };

  const handleAddExpression = setId => {
    const currentSets = JSON.parse(JSON.stringify(sets));
    currentSets[setId].expressions[generateId()] = ExpressionTemplate;
    updateSets(currentSets);
  };

  const handleRemoveExpression = (setId, expressionId) => {
    const currentSets = JSON.parse(JSON.stringify(sets));
    delete currentSets[setId].expressions[expressionId];
    updateSets(currentSets);
  };

  const handleUpdateExpression = (setId, expression) => {
    //This does a deep clone so that state isn't being transfered.
    const currentSets = JSON.parse(JSON.stringify(sets));
    const currentExpression = currentSets[setId].expressions[expression.id];
    currentSets[setId].expressions[expression.id] = {
      ...currentExpression,
      ...expression
    };
    updateSets(currentSets);
  };

  const handleUpdateFilterOperator = (currentMustMatchAll) => {
    setMustMatchAll(currentMustMatchAll)
    onChangeLocal({[FieldNames.customFilter.mustMatchAll]: currentMustMatchAll, [FieldNames.customFilter.expression]: expression, [FieldNames.customFilter.sets]: sets})
  };

  if (!fields) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ border: `1px solid ${ColorConstants.LessLightGrayAlien}`, borderRadius: 5, padding: '10px', backgroundColor: `${ColorConstants.WhiteAlien}`}}>
        <StyledSetHeaderRow>
          <FormControl style={{ marginBottom: '5px', display: 'flex', flexDirection: 'row' }}>
            <label style={{ marginRight: '10px' }}>
              Filter Operator:
            </label>
            <ButtonGroup>
              <Tooltip title="All Expressions Match" enterDelay={400}>
                <Button
                  xs={1}
                  style={{ }}
                  variant={ mustMatchAll ? "contained" : "outlined" }
                  onClick={() => {handleUpdateFilterOperator(true)}}
                >
                  AND
                </Button>
              </Tooltip>
              <Tooltip title="Any Expressions Match" enterDelay={400}>
                <Button
                  xs={1}
                  variant={ !mustMatchAll ? "contained" : "outlined" }
                  onClick={() => {handleUpdateFilterOperator(false)}}
                >
                  OR
                </Button>
              </Tooltip>
            </ButtonGroup>
          </FormControl>
        </StyledSetHeaderRow>
        <CustomFilterSets 
          sets={sets} 
          setSets={setSets} 
          onChange={onChangeLocal} 
          fields={fields} 
          disableUnique={disableUnique} 
          handleUpdateExpression={handleUpdateExpression} 
          handleUpdateSetOperator={handleUpdateSetOperator} 
          handleRemoveSet={handleRemoveSet} 
          handleAddExpression={handleAddExpression}
          handleRemoveExpression={handleRemoveExpression} 
          expressionTemplate={ExpressionTemplate}
          userInfo={userInfo}
          datasetSelection={datasetSelection}
        />
        <StyledAddSetButton onClick={handleAddSet}>
          <PlusIcon fill={theme.palette.gray} /> Add Set
        </StyledAddSetButton>
        <FilterPreviewer
          options={{
            [FieldNames.customFilter.mustMatchAll]: mustMatchAll,
            [FieldNames.customFilter.sets]: sets
          }}
        />
      </div>
    </ThemeProvider>
  );
}
